import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { sortBy } from 'lodash';
import { EMPTY, Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { tryGetErrorMessage } from '../error.utils';
import { UserSearchOptions, UserSearchResult } from '../types';
import { NotifyService } from './notify.service';

@Injectable({
  providedIn: 'root',
})
export class UserSearchService {
  private apiHost = `${environment.apiBaseUrl}`;
  private client = inject(HttpClient);
  private notifyService = inject(NotifyService);
  private route = inject(ActivatedRoute);

  public isLoading = signal<boolean>(false);
  public searchError = signal<string>('');
  public foundUsers = signal<UserSearchResult[]>([]);

  public search(searchQuery: string = '', { by, locationIds }: UserSearchOptions) {
    searchQuery = (searchQuery || '').trim();
    locationIds = locationIds?.filter(Boolean) || [];
    this.searchError.set('');
    this.foundUsers.set([]);

    //Accept either empty or at least 3 characters.
    if (searchQuery.length > 0 && searchQuery.length < 3) {
      this.searchError.set('You need to input 3 or more characters');
      return;
    }

    this.isLoading.set(true);

    const onSuccess = (results: UserSearchResult[]) => {
      const sortedUsers = sortBy(results, 'email');
      this.foundUsers.set(sortedUsers);
      // not really sure why we're doing this (I didn't do this)
      setTimeout(() => this.isLoading.set(false), 1000);
    };

    const onError = (err: any) => {
      this.notifyService.notify(`Failed to search for users: ${tryGetErrorMessage(err)}`);
      return EMPTY;
    };

    switch (by) {
      case 'branches':
        return this.searchUsersByBranches(searchQuery).pipe(catchError(onError)).subscribe(onSuccess);
      case 'locations':
        if (!searchQuery && (!locationIds || locationIds.length === 0)) return EMPTY;
        return this.searchUsersByLocations(locationIds, searchQuery).pipe(catchError(onError)).subscribe(onSuccess);
      default:
        return EMPTY;
    }
  }

  searchUsersByBranches(email: string): Observable<UserSearchResult[]> {
    const params = new HttpParams({ fromObject: { email } });
    const headers = this.getHeaders();
    return this.client.get<UserSearchResult[]>(`${this.apiHost}/UserManagement/UsersByBranches`, { params, headers });
  }

  searchUsersByLocations(location_ids: number[], email?: string) {
    const params = new HttpParams({ fromObject: { location_ids, email } });
    const headers = this.getHeaders();
    return this.client.get<UserSearchResult[]>(`${this.apiHost}/UserManagement/UsersByLocations`, { params, headers });
  }

  private getHeaders() {
    const apiKey = this.route.snapshot.queryParams.api_key;
    return apiKey ? { 'x-api-key': apiKey } : {};
  }
}
